import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { ApiError, apiClient, storePath } from '@/lib/api-client';
import type { PublishOptions, StoreListing, StorePlatform } from '@/types/store';

import { storeKeys } from './store-keys';

export interface PublishListingVars {
  platform: StorePlatform;
  locale: string;
  options?: PublishOptions;
}

/**
 * POST …/listings/:platform/:locale/publish — push the saved copy to the store.
 * A 503 (store credentials not configured) surfaces as an `ApiError` the caller
 * handles specifically; the query cache is only invalidated on success.
 */
export function usePublishListing(): UseMutationResult<StoreListing, ApiError, PublishListingVars> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ platform, locale, options }) =>
      apiClient.post<StoreListing>(
        storePath(
          `/listings/${platform}/${encodeURIComponent(locale)}/publish`,
          options?.changesNotSentForReview ? { changesNotSentForReview: true } : undefined,
        ),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: storeKeys.all });
    },
  });
}
